import { conf } from '../config/config';
import { getAuthHeaders } from '../util/Auth';
import { DEMO_MODE } from '../util/Constants';
import { getLocalStore, setLocalStore } from '../util/Helpers';
import { tempAccountDashboard, tempCampaignStudio, tempCampaigns } from '../demo/demo';

const apiEndpoint = (path) => `${conf.endpoint.replace(/\/$/, '')}${path}`;
const DEMO_ADVERTISEMENT_STORAGE_KEY = 'sadida-demo-advertisement';
const DEMO_BALANCE_STORAGE_KEY = 'sadida-demo-balance';
const DEMO_CAMPAIGN_STORAGE_KEY = 'sadida-demo-campaign';

export const CAMPAIGN_BUDGETS = Object.freeze({
    1: {label: '3 Days', days: 3},
    2: {label: '7 Days', days: 7},
    3: {label: '30 Days', days: 30}
});

export const campaignBudgetFromPlan = (value) => {
    const plan = typeof value === 'object' ? value?.name || value?.label || value?.value : value;
    if (typeof plan === 'number' && CAMPAIGN_BUDGETS[plan]) return plan;
    const normalized = String(plan || '').trim().toLowerCase();
    return Object.entries(CAMPAIGN_BUDGETS).find(([, details]) => details.label.toLowerCase() === normalized)?.[0] || null;
};

export const campaignPlanLabel = (budget) => CAMPAIGN_BUDGETS[Number(budget)]?.label || '';
const normalizeCampaignStatus = (value) => {
    const status = String(value || '').trim().toLowerCase();
    if (['auto', 'active'].includes(status)) return status;
    if (['pending', 'queued', 'processing', 'review'].includes(status)) return 'pending';
    if (['completed', 'complete', 'finished', 'expired'].includes(status)) return 'completed';
    return 'inactive';
};
const campaignDate = (...values) => {
    const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
    if (!value) return null;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
};

const campaignExpiration = (budget, startsAt = Date.now()) => {
    const days = CAMPAIGN_BUDGETS[Number(budget)]?.days;
    const startTimestamp = new Date(startsAt).getTime();
    return days && Number.isFinite(startTimestamp)
        ? new Date(startTimestamp + days * 24 * 60 * 60 * 1000).toISOString()
        : null;
};

const campaignQueue = (campaigns = []) => {
    const active = campaigns.find((campaign) => ['active', 'auto'].includes(normalizeCampaignStatus(campaign.status)));
    const pending = campaigns.filter((campaign) => normalizeCampaignStatus(campaign.status) === 'pending');
    return active ? [active, ...pending.filter((campaign) => campaign !== active)] : pending;
};

const nextCampaignStart = (campaigns = []) => {
    const queue = campaignQueue(campaigns);
    const previousCampaign = queue[queue.length - 1];
    const previousEnd = campaignDate(previousCampaign?.expiresAt, previousCampaign?.data?.expiresAt);
    const now = Date.now();

    // A stale campaign should not leave a new pending campaign scheduled in the past.
    return previousEnd && new Date(previousEnd).getTime() > now
        ? previousEnd
        : new Date(now).toISOString();
};

const normalizeCampaign = (campaign = {}) => {
    let data = campaign.data;
    if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (error) { data = {}; }
    }
    data = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const budget = campaignBudgetFromPlan(campaign.budget ?? campaign.plan ?? campaign.advertisingPlan ?? data.budget ?? data.plan ?? data.advertisingPlan);
    const startsAt = campaignDate(campaign.startsAt, campaign.startAt, campaign.startDate, data.startsAt, data.startAt, data.startDate);
    const expiresAt = campaignDate(campaign.expiresAt, campaign.endAt, campaign.endDate, data.expiresAt, data.endAt, data.endDate);
    return {
        ...campaign,

        ...(budget ? {budget: Number(budget)} : {}),
        status: normalizeCampaignStatus(campaign.status),
        data,
        startsAt,
        expiresAt
    };
};

export const resequenceCampaignQueue = (campaigns = []) => {
    const nextCampaigns = Array.isArray(campaigns) ? campaigns.map((campaign) => normalizeCampaign(campaign)) : [];
    const activeCampaign = nextCampaigns.find((campaign) => ['active', 'auto'].includes(campaign.status));
    let previousEnd = campaignDate(activeCampaign?.expiresAt, activeCampaign?.data?.expiresAt);

    nextCampaigns.forEach((campaign, index) => {
        if (campaign.status !== 'pending') return;

        const startsAt = previousEnd || campaignDate(campaign.startsAt, campaign.data?.startsAt) || new Date(Date.now()).toISOString();
        const expiresAt = campaignExpiration(campaign.budget, startsAt) || campaignDate(campaign.expiresAt, campaign.data?.expiresAt);
        nextCampaigns[index] = normalizeCampaign({
            ...campaign,
            startsAt,
            expiresAt,
            data: {
                ...campaign.data,
                startsAt,
                expiresAt
            }
        });
        if (expiresAt) previousEnd = expiresAt;
    });

    return nextCampaigns;
};

const readDemoCampaigns = () => {
    const stored = getLocalStore(DEMO_CAMPAIGN_STORAGE_KEY);
    if (!stored) return tempCampaigns.map(normalizeCampaign);

    try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed.map(normalizeCampaign);
        return [normalizeCampaign({...tempCampaigns[0], ...parsed})];
    } catch (error) {
        return tempCampaigns.map(normalizeCampaign);
    }
};

const readDemoCampaign = () => readDemoCampaigns()[0] || normalizeCampaign(tempCampaigns[0] || {});

const writeDemoCampaigns = (campaigns) => setLocalStore(DEMO_CAMPAIGN_STORAGE_KEY, campaigns, true);

const readDemoAdvertisementUpdates = () => {
    const stored = getLocalStore(DEMO_ADVERTISEMENT_STORAGE_KEY)
    if (!stored) return {}

    try {
        return JSON.parse(stored) || {}
    } catch (error) {
        return {}
    }
};

const getDemoAdvertisementModel = (modelUuid, updates = {}) => {
    const storedModel = readDemoAdvertisementUpdates();

    return {
        ...tempAccountDashboard.model,
        ...storedModel,
        uuid: modelUuid || storedModel.uuid || tempAccountDashboard.model.uuid,
        ...updates
    };
};

const premiumFrom = (source = {}) => {
    const pending = [source];
    const seen = new Set();
    const nestedKeys = ['data', 'balance', 'accountBalance', 'account', 'advertisement', 'model', 'subscription', 'billing'];

    while (pending.length) {
        const current = pending.shift();
        if (!current || typeof current !== 'object' || seen.has(current)) continue;
        seen.add(current);

        if (current.premium && typeof current.premium === 'object') return current.premium;
        nestedKeys.forEach((key) => {
            if (current[key] && typeof current[key] === 'object') pending.push(current[key]);
        });
    }

    return null;
};

export const premiumBalanceAfterPurchase = (source = {}) => {
    const premium = premiumFrom(source);
    const value = Number(premium?.advertisingBalanceAfterPurchase);
    return Number.isFinite(value) && value >= 0 ? value : null;
};

export const normalizeBalance = (accountBalance = {}) => {
    const nestedBalance = accountBalance?.balance && typeof accountBalance.balance === 'object' ? accountBalance.balance : {};
    const nestedData = accountBalance?.data && typeof accountBalance.data === 'object' ? accountBalance.data : {};
    const balance = {...nestedData, ...nestedBalance, ...accountBalance};
    const premium = premiumFrom(accountBalance);
    const premiumBalance = premiumBalanceAfterPurchase(accountBalance);
    const advertisingBalance = premiumBalance !== null
        ? premiumBalance
        : Number(balance.advertisingBalance ?? balance.campaignBalance ?? 0);

    return {
        ...balance,
        ...(premium ? {premium} : {}),
        availableBalance: Number(balance.availableBalance || 0),
        ...(balance.customerId == null && balance.externalId == null ? {} : {customerId: balance.customerId ?? balance.externalId}),
        advertisingBalance,
        ...(premiumBalance !== null ? {campaignBalance: advertisingBalance} : {}),
        ...(balance.campaignSpend == null ? {} : {campaignSpend: Number(balance.campaignSpend)}),
        ...(Array.isArray(balance.paymentMethods) ? {
            paymentMethods: balance.paymentMethods.map((method) => ({
                ...method,
                id: method.id || method.uuid,
                default: method.default ?? method.isDefault ?? false
            }))
        } : {})
    };
};

const readDemoBalance = () => {
    const stored = getLocalStore(DEMO_BALANCE_STORAGE_KEY);
    if (!stored) return normalizeBalance(tempAccountDashboard.balance);

    try {
        return normalizeBalance(JSON.parse(stored));
    } catch (error) {
        return normalizeBalance(tempAccountDashboard.balance);
    }
};

const writeDemoBalance = (balance) => setLocalStore(DEMO_BALANCE_STORAGE_KEY, normalizeBalance(balance), true);

async function requestJson(path, fallbackMessage, options = {}) {
    const response = await fetch(apiEndpoint(path), {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
            ...(options.headers || {})
        }
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(result.error?.message || fallbackMessage);
    }

    return result.data;
}

export async function getAccountBalance() {
    if (getLocalStore(DEMO_MODE, false, true)) {
        return readDemoBalance();
    }

    const accountBalance = await requestJson('/api/account/balance', 'We could not load your account balance. Please try again.');
    return normalizeBalance(accountBalance);
}

export async function syncAccountPaymentMethods() {
    if (getLocalStore(DEMO_MODE, false, true)) {
        return readDemoBalance();
    }

    const accountBalance = await requestJson('/api/account/balance/payment-methods/sync', 'We could not refresh your payment methods. Please try again.', {
        method: 'POST'
    });
    return normalizeBalance(accountBalance);
}

export async function transferAccountBalance(amount) {
    if (getLocalStore(DEMO_MODE, false, true)) {
        const accountBalance = normalizeBalance(tempAccountDashboard.balance);
        const transferAmount = Number(amount);
        return {
            ...accountBalance,
            availableBalance: accountBalance.availableBalance - transferAmount,
            advertisingBalance: accountBalance.advertisingBalance + transferAmount,
            campaignBalance: accountBalance.advertisingBalance + transferAmount,
            transaction: {id: Date.now(), description: 'Transfer to Ad Balance', amount: -transferAmount, date: new Date().toISOString().slice(0, 10), type: 'transfer'}
        };
    }

    const result = await requestJson('/api/account/balance/transfer', 'We could not transfer funds to your advertising balance. Please try again.', {
        method: 'POST',
        body: JSON.stringify({amount})
    });
    return normalizeBalance(result);
}

export async function depositAccountBalance(amount, paymentMethodId) {
    if (getLocalStore(DEMO_MODE, false, true)) {
        const accountBalance = normalizeBalance(tempAccountDashboard.balance);
        const depositAmount = Number(amount);
        const depositMethod = accountBalance.paymentMethods?.find((method) => String(method.id) === String(paymentMethodId) || String(method.uuid) === String(paymentMethodId) || String(method.externalId) === String(paymentMethodId));
        return {
            ...accountBalance,
            availableBalance: accountBalance.availableBalance + depositAmount,
            transaction: {
                id: Date.now(),
                description: `Deposit via **** ${depositMethod?.last4 || 'card'}`,
                amount: depositAmount,
                date: new Date().toISOString().slice(0, 10),
                type: 'deposit'
            }
        };
    }

    const result = await requestJson('/api/account/balance/deposit', 'We could not process your deposit. Please try again.', {
        method: 'POST',
        body: JSON.stringify({amount, paymentMethodId})
    });
    return normalizeBalance(result);
}

const dashboardCityUuid = (model) => {
    const city = model?.city;
    return typeof city === 'object' ? city.uuid || city.id : city;
};
const isUuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
const normalizeDashboardModel = async (model) => {
    const normalizedModel = normalizeAdvertisementData(model);
    if (!isUuid(dashboardCityUuid(normalizedModel))) return normalizedModel;

    try {
        const cityResult = await requestJson('/api/cities', 'We could not load the city list. Please try again.');
        return normalizeAdvertisementCities(normalizedModel, Array.isArray(cityResult) ? cityResult : []);
    } catch {
        return normalizedModel;
    }
};

export async function getAccountDashboard() {
    if (getLocalStore(DEMO_MODE, false, true)) {
        const model = getDemoAdvertisementModel();
        return {
            ...tempAccountDashboard,
            model,
            balance: normalizeBalance({...readDemoBalance(), ...(model.premium ? {premium: model.premium} : {})})
        };
    }

    const dashboard = await requestJson('/api/account/dashboard', 'We could not load your account. Please try again.');
    const modelReference = dashboard?.user?.model;
    const modelUuid = typeof modelReference === 'string' ? modelReference : modelReference?.uuid;
    const normalizedModel = dashboard?.model ? await normalizeDashboardModel(dashboard.model) : null;
    const normalizedDashboard = {
        ...dashboard,
        balance: normalizeBalance({
            ...dashboard?.balance,
            ...(premiumBalanceAfterPurchase(normalizedModel) !== null ? {premium: normalizedModel.premium} : {})
        }),
        ...(normalizedModel ? {model: normalizedModel} : {})
    };

    if (normalizedDashboard?.model || !modelUuid) {
        return normalizedDashboard;
    }

    const model = await normalizeDashboardModel(await requestJson(`/api/account/ad/${encodeURIComponent(modelUuid)}`, 'We could not load your model profile. Please try again.'));
    return {
        ...normalizedDashboard,
        model,
        balance: normalizeBalance({
            ...normalizedDashboard.balance,
            ...(premiumBalanceAfterPurchase(model) !== null ? {premium: model.premium} : {})
        })
    };
}

export async function setAccountDefaultPaymentMethod(paymentMethodId) {
    if (getLocalStore(DEMO_MODE, false, true)) {
        const accountBalance = normalizeBalance(tempAccountDashboard.balance);
        return {
            ...accountBalance,
            paymentMethods: (accountBalance.paymentMethods || []).map((method) => ({
                ...method,
                default: String(method.id) === String(paymentMethodId)
            }))
        };
    }

    const accountBalance = await requestJson('/api/account/balance/payment-methods/default', 'We could not update your default payment method. Please try again.', {
        method: 'POST',
        body: JSON.stringify({paymentMethodId})
    });
    return normalizeBalance(accountBalance);
}

const campaignRecords = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    if (Array.isArray(payload.campaigns)) return payload.campaigns;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.data?.campaigns)) return payload.data.campaigns;
    const singleCampaign = payload.activeCampaign || payload.currentCampaign || payload.campaign;
    return singleCampaign && typeof singleCampaign === 'object' ? [singleCampaign] : [];
};

export async function getAccountCampaigns() {
    if (getLocalStore(DEMO_MODE, false, true)) return readDemoCampaigns();
    const campaigns = await requestJson('/api/account/campaigns', 'We could not load your campaigns. Please try again.');
    return campaignRecords(campaigns).map(normalizeCampaign);
}

export async function getAccountCampaign() {
    const campaigns = await getAccountCampaigns();
    return campaigns[0] || null;
}

const campaignTransactionEndpoint = '/api/account/transactions';

const logCampaignTransaction = async ({campaignUuid, budget, operation, outcome, error}) => {
    try {
        await requestJson(campaignTransactionEndpoint, 'We could not log your campaign transaction.', {
            method: 'POST',
            body: JSON.stringify({
                type: 'campaign',
                operation,
                outcome,
                ...(campaignUuid ? {campaignUuid} : {}),
                ...(budget ? {budget} : {}),
                ...(error ? {error: error.message || String(error)} : {})
            })
        });
    } catch {
        // Transaction logging must not hide the original campaign result.
    }
};

export async function createAccountCampaign({budget = 2, status = 'inactive'} = {}) {
    const normalizedBudget = Number(campaignBudgetFromPlan(budget));
    const requestedStatus = normalizeCampaignStatus(status);
    const input = {budget: normalizedBudget, status: requestedStatus};
    if (getLocalStore(DEMO_MODE, false, true)) {
        const existingCampaigns = readDemoCampaigns();
        const startsAt = requestedStatus === 'pending' ? nextCampaignStart(existingCampaigns) : new Date(Date.now()).toISOString();
        const expiresAt = campaignExpiration(normalizedBudget, startsAt);
        const campaign = normalizeCampaign({
            uuid: `demo-campaign-${Date.now()}-${normalizedBudget}-${existingCampaigns.length}`,
            userUuid: tempAccountDashboard.user?.uuid,
            name: `${tempAccountDashboard.model?.name || 'Model'} next campaign`,
            modelSlug: tempAccountDashboard.model?.slug,
            ...input,
            startsAt,
            expiresAt,
            data: {startsAt, expiresAt}
        });
        writeDemoCampaigns([...existingCampaigns, campaign]);
        return campaign;
    }

    let campaign;
    let campaignError;
    try {
        const createdCampaign = normalizeCampaign(await requestJson('/api/account/campaigns', 'We could not create your campaign. Please try again.', {
            method: 'POST',
            body: JSON.stringify(input)
        }));
        campaign = normalizeCampaign({...createdCampaign, status: requestedStatus});

        // Some campaign APIs default newly created records to inactive. Explicitly
        // persist the requested active or pending state when the response disagrees.
        if (['active', 'pending'].includes(requestedStatus) && createdCampaign.uuid && createdCampaign.status !== requestedStatus) {
            campaign = await updateAccountCampaign(createdCampaign.uuid, {budget: normalizedBudget, status: requestedStatus}, {logTransaction: false});
        }
        return campaign;
    } catch (error) {
        campaignError = error;
        throw error;
    } finally {
        // Pending campaigns are only scheduled. Do not create a financial
        // transaction until the campaign becomes active.
        if (requestedStatus !== 'pending') {
            await logCampaignTransaction({
                campaignUuid: campaign?.uuid,
                budget: normalizedBudget,
                operation: 'create',
                outcome: campaignError ? 'failed' : 'completed',
                error: campaignError
            });
        }
    }
}

export async function updateAccountCampaign(campaignUuid, updates = {}, {logTransaction = true} = {}) {
    const budget = Object.prototype.hasOwnProperty.call(updates, 'budget') || Object.prototype.hasOwnProperty.call(updates, 'plan')
        ? Number(campaignBudgetFromPlan(updates.budget ?? updates.plan))
        : null;
    const payload = {
        ...(Object.prototype.hasOwnProperty.call(updates, 'status') ? {status: String(updates.status).toLowerCase()} : {}),
        ...(budget ? {budget} : {}),
        ...(updates.data ? {data: updates.data} : {})
    };

    if (getLocalStore(DEMO_MODE, false, true)) {
        const campaigns = readDemoCampaigns();
        const currentIndex = campaigns.findIndex((campaign) => String(campaign.uuid) === String(campaignUuid));
        const fallbackIndex = currentIndex >= 0 ? currentIndex : 0;
        const current = campaigns[fallbackIndex] || readDemoCampaign();
        const isPending = normalizeCampaignStatus(current.status) === 'pending';
        const isAutoPay = normalizeCampaignStatus(current.status) === 'auto';
        const startsAt = budget
            ? (isPending
                ? campaignDate(current.startsAt, current.data?.startsAt) || new Date(Date.now()).toISOString()
                : isAutoPay
                    ? campaignDate(current.startsAt, current.data?.startsAt)
                    : new Date(Date.now()).toISOString())
            : campaignDate(current.startsAt, current.data?.startsAt);
        const expiresAt = budget
            ? (isAutoPay
                ? campaignDate(current.expiresAt, current.data?.expiresAt)
                : campaignExpiration(budget, startsAt || Date.now()))
            : campaignDate(current.expiresAt, current.data?.expiresAt);
        const next = normalizeCampaign({
            ...current,
            ...payload,
            ...(startsAt ? {startsAt} : {}),
            ...(expiresAt ? {expiresAt} : {}),
            data: {
                ...current.data,
                ...(payload.data || {}),
                ...(startsAt ? {startsAt} : {}),
                ...(expiresAt ? {expiresAt} : {})
            }
        });
        const nextCampaigns = [...campaigns];
        nextCampaigns[fallbackIndex] = next;
        writeDemoCampaigns(nextCampaigns);
        return next;
    }

    let campaign;
    let campaignError;
    try {
        if (!campaignUuid) throw new Error('We could not identify your campaign. Please try again.');
        campaign = normalizeCampaign({
            ...(await requestJson(`/api/account/campaigns/${encodeURIComponent(campaignUuid)}`, 'We could not save your campaign. Please try again.', {
                method: 'PATCH',
                body: JSON.stringify(payload)
            })),
            ...(payload.status ? {status: payload.status} : {})
        });
        return campaign;
    } catch (error) {
        campaignError = error;
        throw error;
    } finally {
        if (logTransaction && String(updates.status || '').toLowerCase() !== 'pending') {
            await logCampaignTransaction({
                campaignUuid: campaign?.uuid || campaignUuid,
                budget,
                operation: 'update',
                outcome: campaignError ? 'failed' : 'completed',
                error: campaignError
            });
        }
    }
}

export async function deleteAccountCampaign(campaignUuid) {
    if (!campaignUuid) throw new Error('We could not identify your campaign. Please try again.');

    if (getLocalStore(DEMO_MODE, false, true)) {
        const campaigns = readDemoCampaigns();
        const campaignIndex = campaigns.findIndex((campaign) => String(campaign.uuid) === String(campaignUuid));
        const campaign = campaigns[campaignIndex];

        if (!campaign) throw new Error('We could not find your campaign. Please try again.');
        if (campaign.status !== 'pending') throw new Error('Only pending campaigns can be deleted.');

        const nextCampaigns = resequenceCampaignQueue(campaigns.filter((_, index) => index !== campaignIndex));
        writeDemoCampaigns(nextCampaigns);
        return {uuid: campaignUuid, campaigns: nextCampaigns};
    }

    let deletedCampaign;
    let campaignError;
    try {
        if (!campaignUuid) throw new Error('We could not identify your campaign. Please try again.');
        deletedCampaign = await requestJson(`/api/account/campaigns/${encodeURIComponent(campaignUuid)}`, 'We could not delete your campaign. Please try again.', {
            method: 'DELETE'
        });
        return deletedCampaign || {uuid: campaignUuid};
    } catch (error) {
        campaignError = error;
        throw error;
    } finally {
        await logCampaignTransaction({
            campaignUuid: deletedCampaign?.uuid || campaignUuid,
            budget: deletedCampaign?.budget,
            operation: 'delete',
            outcome: campaignError ? 'failed' : 'completed',
            error: campaignError
        });
    }
}

const advertisementContactKeys = ['whatsapp', 'telegram', 'phone', 'email'];

const normalizeAdvertisementContacts = (advertisement = {}) => {
    const nestedContacts = advertisement.contacts && typeof advertisement.contacts === 'object' && !Array.isArray(advertisement.contacts)
        ? advertisement.contacts
        : {};
    const contacts = {...nestedContacts};

    advertisementContactKeys.forEach((key) => {
        if ((contacts[key] === undefined || contacts[key] === null || contacts[key] === '') && advertisement[key] !== undefined && advertisement[key] !== null) {
            contacts[key] = advertisement[key];
        }
    });

    return {...advertisement, contacts};
};

const normalizeAdvertisementData = (advertisement = {}) => {
    const model = advertisement.model && typeof advertisement.model === 'object' && !Array.isArray(advertisement.model)
        ? advertisement.model
        : {};
    const source = {...model, ...advertisement};
    let nestedData = source.data;

    if (typeof nestedData === 'string') {
        try {
            nestedData = JSON.parse(nestedData);
        } catch (error) {
            nestedData = null;
        }
    }

    if (!nestedData || typeof nestedData !== 'object' || Array.isArray(nestedData)) return source;
    return {...source, ...nestedData};
};

export async function getAccountAdvertisement(modelUuid) {
    if (getLocalStore(DEMO_MODE, false, true)) {
        return normalizeAdvertisementContacts({
            ...getDemoAdvertisementModel(modelUuid),
            ...tempCampaignStudio,
            photos: [...tempCampaignStudio.photos],
            contacts: {
                ...tempAccountDashboard.model.contacts,
                ...(getDemoAdvertisementModel(modelUuid).contacts || {})
            }
        });
    }

    const path = modelUuid ? `/api/account/ad/${encodeURIComponent(modelUuid)}` : '/api/account/ad';
    const advertisement = await requestJson(path, 'We could not load your advertisement. Please try again.');
    let cities = [];
    try {
        const cityResult = await requestJson('/api/cities', 'We could not load the city list. Please try again.');
        cities = Array.isArray(cityResult) ? cityResult : [];
    } catch {
        // City labels are supplementary; do not hide a valid advertisement or Premium state if this request fails.
    }

    return normalizeAdvertisementContacts(normalizeAdvertisementCities(normalizeAdvertisementData(advertisement), cities));
}

const normalizeAdvertisementUpdates = (updates = {}) => {
    const hasCity = Object.prototype.hasOwnProperty.call(updates, 'city');
    const hasSingleCity = Object.prototype.hasOwnProperty.call(updates, 'singleCity');
    const hasTelegram = Object.prototype.hasOwnProperty.call(updates, 'telegram');
    const {telegram, contacts, ...details} = updates;
    const normalizedDetails = !hasCity && hasSingleCity ? {...details, city: details.singleCity} : details;

    if (!hasTelegram) return contacts === undefined ? normalizedDetails : {...normalizedDetails, contacts};

    return {
        ...normalizedDetails,
        contacts: {
            ...(contacts && typeof contacts === 'object' && !Array.isArray(contacts) ? contacts : {}),
            telegram
        }
    };
};

const advertisingPlanDurations = {
    '3 days': 3,
    '7 days': 7,
    '30 days': 30
};

const withAdvertisementExpiration = (updates = {}) => {
    if (!Object.prototype.hasOwnProperty.call(updates, 'plan')) return updates;

    const planValue = typeof updates.plan === 'string'
        ? updates.plan
        : updates.plan?.name || updates.plan?.label || updates.plan?.value;
    const plan = String(planValue || '').trim().toLowerCase();
    const durationDays = advertisingPlanDurations[plan];
    if (!durationDays) return updates;

    return {
        ...updates,
        expiresAt: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString()
    };
};

export async function updateAccountAdvertisement(modelUuid, updates) {
    const normalizedUpdates = withAdvertisementExpiration(normalizeAdvertisementUpdates(updates));

    if (getLocalStore(DEMO_MODE, false, true)) {
        if (Object.keys(normalizedUpdates).length > 0) {
            const persistedModel = getDemoAdvertisementModel(modelUuid, normalizedUpdates);
            setLocalStore(DEMO_ADVERTISEMENT_STORAGE_KEY, persistedModel, true)
        }
        return {
            ...getDemoAdvertisementModel(modelUuid),
            ...tempCampaignStudio,
            photos: [...tempCampaignStudio.photos],
            contacts: {
                ...tempAccountDashboard.model.contacts,
                ...(getDemoAdvertisementModel(modelUuid).contacts || {})
            },
            ...normalizedUpdates
        };
    }

    const savedAdvertisement = await requestJson(`/api/account/ad/${encodeURIComponent(modelUuid)}`, 'We could not save your advertisement. Please try again.', {
        method: 'PATCH',
        body: JSON.stringify({model: {data: normalizedUpdates}})
    });
    return normalizeAdvertisementData(savedAdvertisement);
}

export async function purchaseAccountPremium(modelUuid, {paymentMethodId = null, advertisingBalance = null, advertisingBalanceApplied = 0, cardCharge = 0, free = false} = {}) {
    if (!modelUuid) throw new Error('We could not identify your model profile. Please try again.');

    const now = Date.now();
    const startsAt = new Date(now).toISOString();
    const expiresAt = new Date(now + 30 * 24 * 60 * 60 * 1000).toISOString();
    const currentAdvertisingBalanceValue = Number(advertisingBalance);
    const currentAdvertisingBalance = advertisingBalance !== null
        && advertisingBalance !== undefined
        && advertisingBalance !== ''
        && Number.isFinite(currentAdvertisingBalanceValue)
        ? currentAdvertisingBalanceValue
        : null;
    const requestedBalanceApplied = Math.max(Number(advertisingBalanceApplied) || 0, 0);
    const balanceApplied = Number.isFinite(currentAdvertisingBalance)
        ? Math.min(requestedBalanceApplied, Math.max(currentAdvertisingBalance, 0))
        : requestedBalanceApplied;
    const advertisingBalanceAfterPurchase = Number.isFinite(currentAdvertisingBalance)
        ? Math.max(currentAdvertisingBalance - balanceApplied, 0)
        : null;
    const premium = {
        product: 'Order Premium Model',
        status: 'active',
        price: free ? 0 : 49,
        ...(free ? {source: 'first-campaign', freeOfCharge: true} : {}),
        advertisingBalanceApplied: balanceApplied,
        ...(advertisingBalanceAfterPurchase !== null ? {advertisingBalanceAfterPurchase} : {}),
        cardCharge: Number(cardCharge) || 0,
        ...(paymentMethodId ? {paymentMethodId} : {}),
        startsAt,
        expiresAt
    };
    const updates = {
        premium,
        ...(advertisingBalanceAfterPurchase !== null ? {advertisingBalance: advertisingBalanceAfterPurchase} : {})
    };

    const savedAdvertisement = await updateAccountAdvertisement(modelUuid, updates);
    const normalizedSavedAdvertisement = normalizeAdvertisementData(savedAdvertisement || {});
    if (getLocalStore(DEMO_MODE, false, true) && advertisingBalanceAfterPurchase !== null) {
        const accountBalance = readDemoBalance();
        writeDemoBalance({
            ...accountBalance,
            advertisingBalance: advertisingBalanceAfterPurchase,
            campaignBalance: advertisingBalanceAfterPurchase
        });
    }
    return {
        ...normalizedSavedAdvertisement,
        premium: normalizedSavedAdvertisement.premium || premium,
        ...(advertisingBalanceAfterPurchase !== null ? {advertisingBalance: advertisingBalanceAfterPurchase} : {})
    };
}

export async function grantFirstCampaignPremium(modelUuid) {
    return purchaseAccountPremium(modelUuid, {
        advertisingBalanceApplied: 0,
        cardCharge: 0,
        free: true
    });
}

const normalizeAdvertisementCities = (advertisement = {}, cities = []) => {
    const cityName = (value) => {
        if (!value) return value;
        const reference = typeof value === 'object' ? value.uuid || value.name || value.slug : value;
        const city = cities.find((item) => [item.uuid, item.name, item.slug].some((candidate) => candidate && String(candidate).toLowerCase() === String(reference).toLowerCase()));
        return city?.name || (typeof value === 'object' ? value.name || value.uuid : value);
    };

    return {
        ...advertisement,
        city: cityName(advertisement.city),
        singleCity: cityName(advertisement.singleCity),
        tourSchedule: Array.isArray(advertisement.tourSchedule)
            ? advertisement.tourSchedule.map((stop) => ({...stop, city: cityName(stop.city)}))
            : advertisement.tourSchedule
    };
};

export async function uploadAccountAdvertisementPhoto(modelUuid, file) {
    if (!modelUuid || !file || !file.name || !file.type || !Number.isFinite(file.size)) {
        throw new Error('We could not prepare the photo upload. Please choose a valid image.');
    }

    const upload = await requestJson(`/api/account/ad/${encodeURIComponent(modelUuid)}/photos/upload-url`, 'We could not prepare the photo upload. Please try again.', {
        method: 'POST',
        body: JSON.stringify({filename: file.name, contentType: file.type, size: file.size})
    });

    if (!upload?.uploadUrl || !upload?.photoUrl) {
        throw new Error('We could not prepare the photo upload. Please try again.');
    }

    const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': file.type,
            'Cache-Control': 'public, max-age=31536000, immutable',
            ...(upload.headers || upload.uploadHeaders || {})
        },
        body: file
    });

    if (!uploadResponse.ok) throw new Error('We could not upload the photo to S3. Please try again.');
    return upload.photoUrl;
}

export async function uploadAccountAdvertisementVideo(modelUuid, file) {
    if (!modelUuid || !file || !file.name || !file.type || !Number.isFinite(file.size)) {
        throw new Error('We could not prepare the video upload. Please choose a valid video.');
    }

    const upload = await requestJson(`/api/account/ad/${encodeURIComponent(modelUuid)}/videos/upload-url`, 'We could not prepare the video upload. Please try again.', {
        method: 'POST',
        body: JSON.stringify({filename: file.name, contentType: file.type, size: file.size})
    });

    if (!upload?.uploadUrl || !upload?.videoUrl) {
        throw new Error('We could not prepare the video upload. Please try again.');
    }

    const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': file.type,
            'Cache-Control': 'public, max-age=31536000, immutable',
            ...(upload.headers || upload.uploadHeaders || {})
        },
        body: file
    });

    if (!uploadResponse.ok) throw new Error('We could not upload the video to S3. Please try again.');
    return upload.videoUrl;
}
